/* eslint-disable*/
import {
	useState, useEffect, useRef, useCallback
} from 'react';
import notificationSound from '../sounds/notification.mp3';
import { authServices } from '@/Services';
import { useDispatch, useSelector } from 'react-redux';
import wastageAction from '@/Actions/wastage.action';

export const useSmartNotificationPolling = (userId, fetchOnly = true) => {
	const notifications = useSelector((state) => state?.wastage?.notification?.list || { leftOver: [], orders: [] });
	const unreadCount = useSelector((state) => state?.wastage?.notification?.unreadCount || { leftOver: 0, orders: 0 });
	const [isLoading, setIsLoading] = useState(false);
	const intervalRef = useRef(null);
	const retryCountRef = useRef(0);
	const lastFetchTimeRef = useRef(0);
	const audioRef = useRef(null);
	const [audioInitialized, setAudioInitialized] = useState(false);
	const previousOrdersNotificationCountRef = useRef(new Set());
	const dispatch = useDispatch();

	useEffect(() => {
		const initAudio = async () => {
			try {
				const audio = new Audio(notificationSound);
				audio.volume = 0.7;
				audio.preload = 'auto';

				await new Promise((resolve, reject) => {
					audio.addEventListener('canplaythrough', resolve, {
						once: true,
					});
					audio.addEventListener('error', reject, {
						once: true,
					});

					setTimeout(() => resolve(), 2000);
				});

				audioRef.current = {
					audio,
					play: async () => {
						try {
							audio.currentTime = 0;

							const playPromise = audio.play();
							if (playPromise !== undefined) {
								await playPromise;
							}
						} catch (error) {
							console.warn('Audio play failed:', error.message);

							if (error.name === 'NotAllowedError') {
								console.warn('Audio autoplay blocked. User interaction required.');
							}
						}
					},
				};
				setAudioInitialized(true);
				console.log('Audio initialized successfully');
			} catch (error) {
				console.warn('Could not initialize audio:', error);
				audioRef.current = null;
				setAudioInitialized(false);
			}
		};
		initAudio();
	}, []);

	const getPollingInterval = () => {
		const baseInterval = 300000;
		const maxInterval = 600000;
		if (retryCountRef.current > 0) {
			const backoffMultiplier = Math.min(retryCountRef.current, 5);
			return Math.min(baseInterval * Math.pow(1.5, backoffMultiplier), maxInterval);
		}
		return baseInterval;
	};

	const playNotificationSound = useCallback(() => {
		if (audioRef.current && audioInitialized) {
			audioRef.current.play()
				.then(() => {
					console.log('Sound played successfully');
				})
				.catch((error) => {
					console.error('Failed to play notification sound:', error);
				});
		} else {
			console.warn('Audio not initialized or not available. AudioInitialized:', audioInitialized);
		}
	}, [audioInitialized]);

	const fetchNotifications = useCallback(async () => {
		if (isLoading) return;

		setIsLoading(true);
		try {
			const response = await authServices?.fetchNotification();
			if (response.statusCode === 200) {
				const orderData = response?.data?.notifications || [];
				const leftOverData = response?.data?.leftOverNotifications || [];

				const currentOrdersIds = new Set(orderData.map((n) => {
					if (n?.orders?.length > 0) {
						return n?.orders?.map((order) => order?.id);
					}
					return n?.id;
				}).flat());

				const previousOrdersIds = previousOrdersNotificationCountRef.current || new Set();

				const filteredOrdersNotifications = orderData.map((notification) => {
					if (notification?.orders?.some((order) => order?.toStoreId !== notification?.fromStoreId)) {
						return {
							...notification,
							orders: notification?.orders?.filter((order) => order?.toStoreId !== notification?.fromStoreId),
						};
					}
					return null;
				}).filter((item) => item !== null);

				const newOrdersNotifications = filteredOrdersNotifications.filter((n) => {
					if (n?.orders?.length > 0) {
						return n?.orders?.some((order) => !previousOrdersIds.has(order?.id));
					}
					return !previousOrdersIds.has(n?.id);
				});

				const hasNewOrdersNotifications = newOrdersNotifications.length > 0;
				const newUnreadCount = filteredOrdersNotifications.reduce((acc, n) => {
					if (Array.isArray(n?.orders) && n.orders.length > 0) {
						return acc + n.orders.filter(order => !order?.read).length;
					}
					return acc + (!n?.read ? 1 : 0);
				}, 0);

				const newLeftOverUnreadCount = leftOverData.reduce((acc, n) => {
					if (Array.isArray(n?.leftOvers) && n.leftOvers.length > 0) {
						return acc + n.leftOvers.filter(leftOver => !leftOver?.read).length;
					}
					return acc + (!n?.read ? 1 : 0);
				}, 0);

				dispatch(wastageAction?.notificationList({
					list: {
						orders: filteredOrdersNotifications,
						leftOver: leftOverData,
					},
					unreadCount: {
						orders: newUnreadCount,
						leftOver: newLeftOverUnreadCount,
					},
				}));
				if (hasNewOrdersNotifications && currentOrdersIds.size > 0) {
					console.log('🔔 New notifications detected! Playing sound...');
					console.log('New count:', currentOrdersIds.size, 'Previous count:', previousOrdersIds.size);

					setTimeout(() => {
						playNotificationSound();
					}, 200);

					retryCountRef.current = 0;
				} else {
					retryCountRef.current += 1;
				}
				previousOrdersNotificationCountRef.current = currentOrdersIds;
				lastFetchTimeRef.current = Date.now();
			} else {
				throw new Error(`Failed to fetch notifications: ${response.statusCode}`);
			}
		} catch (error) {
			console.error('Failed to fetch notifications:', error);
			retryCountRef.current += 1;
		} finally {
			setIsLoading(false);
		}
	}, [playNotificationSound, dispatch]);

	const stopPolling = useCallback(() => {
		if (intervalRef.current) {
			clearTimeout(intervalRef.current);
			intervalRef.current = null;
		}
	}, []);

	const startPolling = useCallback(() => {
		stopPolling();
		if (document.visibilityState === 'hidden') {
			return;
		}

		const poll = async () => {
			if (document.visibilityState === 'visible') {
				await fetchNotifications();
				const nextInterval = getPollingInterval();
				console.log(`Next poll in ${nextInterval / 1000}s`);
				intervalRef.current = setTimeout(poll, nextInterval);
			}
		};

		poll();
	}, [fetchNotifications, stopPolling]);

	useEffect(() => {
		if (!userId || !fetchOnly) return;

		if (document.visibilityState === 'visible') {
			startPolling();
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible') {
				startPolling();
			} else {
				stopPolling();
			}
		};

		const handleBeforeUnload = () => {
			stopPolling();
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			stopPolling();
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [userId, startPolling, stopPolling, fetchOnly]);

	const markAsRead = useCallback(async (notificationId, orderId = null, notificationType = 'orders', leftOverId = null) => {
		try {
			const updatedNotifications = notificationType === 'orders' ? notifications.orders.map((n) => {
				if (orderId && n?.orders?.length > 0) {
					return {
						...n,
						orders: n.orders.map(order =>
							order.id === orderId ? { ...order, read: true } : order
						)
					};
				} else if (n.id === notificationId) {
					return { ...n, read: true };
				}
				return n;
			}) : notifications.leftOver.map((n) => {
				if (leftOverId && n?.leftOvers?.length > 0) {
					return {
						...n,
						leftOvers: n.leftOvers.map(leftOver => leftOver.leftOverId === leftOverId ? { ...leftOver, read: true } : leftOver)
					};
				}
				return n;
			});

			const newLeftOverUnreadCount = updatedNotifications.leftOver.reduce((count, n) => {
				if (n?.leftOvers?.length > 0) {
					return count + n.leftOvers.filter(leftOver => !leftOver?.read).length;
				}
				return count + (!n?.read ? 1 : 0);
			}, 0);

			const newUnreadCount = updatedNotifications.reduce((count, n) => {
				if (n?.orders?.length > 0) {
					return count + n.orders.filter(order => !order?.read).length;
				}
				return count + (!n?.read ? 1 : 0);
			}, 0);

			dispatch(wastageAction?.notificationList({
				list: {
					orders: notificationType === 'orders' ? updatedNotifications : notifications.orders,
					leftOver: notificationType === 'leftOver' ? updatedNotifications : notifications.leftOver,
				},
				unreadCount: {
					orders: newUnreadCount,
					leftOver: newLeftOverUnreadCount,
				},
			}));

		} catch (error) {
			console.error('Failed to mark notification as read:', error);
		}
	}, [notifications, dispatch]);

	const testSound = useCallback(() => {
		playNotificationSound();
	}, [playNotificationSound]);

	return {
		notifications,
		unreadCount,
		isLoading,
		markAsRead,
		refetch: fetchNotifications,
		testSound,
		audioInitialized,
	};
};
